// client/src/components/comunicacao/automatica/ComunicacaoAutomaticaPage.js
import React, { useEffect, useState, Fragment, useCallback } from 'react';
import { useComunicacaoConfigs, useMutateComunicacaoConfig } from '../../../hooks/useComunicacaoConfig';
import { useTemplatesMensagem } from '../../../hooks/useTemplateMensagem';
import useAlert from '../../../hooks/useAlert';
import { useAuth } from '../../../hooks/useAuth';
import { isMatrizAdmin as checkIsMatrizAdmin } from '../../../utils/permissions';
import { PlusCircle, Loader2, XCircle, MessageSquare, Eye, Trash2 } from 'lucide-react';
import ComunicacaoAutomaticaForm from './ComunicacaoAutomaticaForm';
import ComunicacaoAutomaticaRow from './ComunicacaoAutomaticaRow';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';

const ComunicacaoAutomaticaPage = () => {
  const { setAlert } = useAlert();
  const { user } = useAuth();
  const isMatrizAdmin = checkIsMatrizAdmin(user) && !user?.account && !user?.franquia;

  const { data: configsData, isLoading, isError, refetch } = useComunicacaoConfigs({ tipo: 'Automatica' });
  const { data: templatesData } = useTemplatesMensagem();
  const templates = templatesData || [];

  const { deleteConfig } = useMutateComunicacaoConfig();

  const [showForm, setShowForm] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);

  const configs = configsData || [];

  useEffect(() => {
    if (isError) setAlert('Erro ao carregar configurações automáticas', 'danger');
  }, [isError, setAlert]);

  const handleAddNew = useCallback(() => {
    setCurrentId(null);
    setShowForm(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setCurrentId(null);
    refetch();
  }, [refetch]);

  const handleEdit = useCallback((item) => {
    setCurrentId(item._id);
    setShowForm(true);
    setSelectedConfig(null);
  }, []);

  const handleDelete = useCallback((id) => {
    if (window.confirm('Deseja realmente excluir esta configuração automática?')) {
      deleteConfig.mutate(id, { onSuccess: () => setSelectedConfig(null) });
    }
  }, [deleteConfig]);

  return (
    <Fragment>
      <div className="page-management-container">
        <PageHeader
          icon={<MessageSquare />}
          title="Comunicação Automática"
          subtitle={!isLoading && !showForm && configs.length > 0 ? `${configs.length} configuração${configs.length !== 1 ? 'ões' : ''}` : null}
          actions={showForm && (
            <button onClick={handleCloseForm} className="action-button action-button-secondary">
              <XCircle size={18} className="icon"/> Fechar
            </button>
          )}
        />

        {showForm && (
          <div className="content-panel">
            <ComunicacaoAutomaticaForm
              currentId={currentId}
              templates={templates}
              onCloseForm={handleCloseForm}
              isMatrizAdmin={isMatrizAdmin}
            />
          </div>
        )}
      </div>

      {!showForm && (
        <div className="table-container">
          <div className="table-wrapper">
            {isLoading ? (
              <div className="table-loading-state">
                <Loader2 size={24} className="animate-spin"/>
                <span>Carregando configurações...</span>
              </div>
            ) : configs.length > 0 ? (
              <table className="styled-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Nome</th>
                    <th>Evento</th>
                    <th>Canal</th>
                    <th>Ativo</th>
                    <th className="text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map(config => (
                    <ComunicacaoAutomaticaRow
                      key={config._id}
                      config={config}
                      onOpenActions={setSelectedConfig}
                      isDeleting={deleteConfig.isPending}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="table-empty-state">
                <MessageSquare size={48}/>
                <h3>Nenhuma configuração automática encontrada</h3>
                <p>Clique no botão + para criar sua primeira configuração automática.</p>
                <button onClick={handleAddNew} className="action-button action-button-primary" style={{ marginTop: '1rem' }}>
                  <PlusCircle size={18} className="icon"/> Adicionar Configuração
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <FabButton onClick={handleAddNew} title="Adicionar Configuração" visible={!showForm} icon={<MessageSquare size={26} className="icon"/>} />

      <ActionsDrawer
        open={!!selectedConfig}
        onClose={() => setSelectedConfig(null)}
        title={selectedConfig?.nome || 'Configuração Automática'}
        actions={selectedConfig ? [
          {
            key: 'editar',
            label: 'Visualizar/Editar',
            icon: <Eye size={20} className="icon" />,
            onClick: () => handleEdit(selectedConfig),
          },
          {
            key: 'excluir',
            label: 'Excluir',
            icon: <Trash2 size={20} className="icon" />,
            onClick: () => handleDelete(selectedConfig._id),
            destructive: true,
            loading: deleteConfig.isPending,
          },
        ] : []}
      />
    </Fragment>
  );
};

export default ComunicacaoAutomaticaPage;
